// deps
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// internal deps
import { AriaHidden, FormatTextToHtml } from "@swiggy-private/core-ui";

// constants
import { SAVINGS_BANNER_TEST_IDS } from "@super-ui/test-ids/savings-banner";

// helpers
import { getFormattedNumber } from "@super-ui/helpers/String";
import { PRE_CONFIGURED_FILLERS } from "@super-ui/constants/SwiggyOne";

//styles
import styles from "../styles/CartSavingsBannerOneLine.scss";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FlipText } from "../styles/CartSavingsBannerOneline.style";
import { isEmpty } from "@swiggy-private/js-utils";
import { SAVINGS_TEXTS } from "@super-ui/constants/cartSavings";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

interface CartSavingsOneLineBlackTextContainerProps {
    title: string;
    subtitle: string;
    details: Record<string, any>;
    isExpress?: boolean;
    isOTG?: boolean;
    showFinalTheme?: boolean;
    isBolt?: boolean;
    is99Store?: boolean;
    isEOCart?: boolean;
}

const CartSavingsOneLineBlackTextContainer: React.FC<CartSavingsOneLineBlackTextContainerProps> = ({
    title,
    subtitle,
    details,
    isExpress,
    isOTG,
    showFinalTheme,
    isBolt,
    is99Store,
    isEOCart,
}) => {
    const [flip, setFlip] = useState(false);
    const [time, setTime] = useState<string>("0s");

    const timerRef = useRef<any>(null);

    useEffect(() => {
        if (showFinalTheme) {
            setFlip(false);
            setTime("0s");
        } else {
            if (isEmpty(title)) {
                setFlip(false);
                setTime("1.5s");
            } else if (isExpress) {
                timerRef.current = setTimeout(() => {
                    setFlip(true);
                }, 1900);
                setTime("2s");
            } else if (isBolt || isOTG || is99Store || isEOCart) {
                setTime("1.5s");
            }
        }

        // Cleanup function
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            setFlip(false);
        };
    }, [isExpress, isOTG, showFinalTheme, isBolt, title, is99Store, isEOCart]);

    const renderTitle = useMemo(() => {
        const totalDiscountKey = PRE_CONFIGURED_FILLERS.TOTAL_DISCOUNT;
        const searchString = `{{${totalDiscountKey}}}`;
        const updatedTitle = title.replace(searchString, "");

        return details[totalDiscountKey] ? `₹${details[totalDiscountKey]} ${updatedTitle.trim()}` : updatedTitle;
    }, [title, details]);

    const renderSubtitle = useMemo(() => {
        return Object.keys(details).reduce((acc, key) => {
            const searchString = `{{${key}}}`;
            return key === PRE_CONFIGURED_FILLERS.SUB_DISCOUNT
                ? acc.replace(new RegExp(searchString, "g"), `₹ ${getFormattedNumber(details[key])}`)
                : acc;
        }, subtitle);
    }, [details, subtitle]);

    const getStripTextColor = (
        is99Store: boolean | undefined,
        isBolt: boolean | undefined,
        isEOCart: boolean | undefined,
    ): keyof IColors | undefined => {
        if (is99Store) {
            return "text_High_Emphasis";
        } else if (isBolt || isEOCart) {
            return "text_inverse_Highest_Emphasis";
        }
        return "text_inverse_Med_Emphasis";
    };

    const getBannerTitle = useCallback(() => {
        if (is99Store) {
            return SAVINGS_TEXTS._99STORE;
        } else if (isBolt) {
            return SAVINGS_TEXTS.BOLT;
        } else if (isExpress) {
            return SAVINGS_TEXTS.EXPRESS;
        }
        return SAVINGS_TEXTS.NON_EXPRESS;
    }, [isBolt, isExpress, is99Store]);

    const renderText = useMemo(() => {
        let finalText = "";
        if (isEmpty(title)) {
            finalText = getBannerTitle();
        } else {
            if (!flip && isExpress && !showFinalTheme) {
                finalText = SAVINGS_TEXTS.EXPRESS;
            } else {
                finalText = `${renderTitle} ${renderSubtitle}`;
            }
        }

        return new FormatTextToHtml(finalText).getImage({ imgStyle: styles.titleImage }).val;
    }, [flip, isExpress, renderSubtitle, renderTitle, showFinalTheme, title, getBannerTitle]);

    return (
        <AriaHidden as="div" className={styles.containerForText} style={{ overflow: "hidden" }}>
            <FlipText
                showFlipAnimation={(isExpress || isBolt) && !isEmpty(title)}
                time={time}
                showFinalTheme={showFinalTheme}
                flip={flip}
                isBolt={isBolt}
            >
                <Text
                    data-testid={SAVINGS_BANNER_TEST_IDS.TEXT}
                    variant="V2.body3.neutral.medium"
                    color={getStripTextColor(is99Store, isBolt, isEOCart)}
                    maxLines={1}
                    dangerouslySetInnerHTML={{ __html: renderText }}
                />
            </FlipText>
        </AriaHidden>
    );
};

export default React.memo(CartSavingsOneLineBlackTextContainer);
